import { api } from '@/lib/api';
import type {
  ProcessRow,
  ProcessGraph,
  ProcessAuditEntry,
  ProcessStep,
  ProcessEdge,
  RaciRole,
  KpiRelationship,
  SeatKpiRole,
  ProcessStatus,
} from './types';

const BASE = '/v4/processes';

export interface ListFilter {
  status?: ProcessStatus;
  category?: string;
  includeArchived?: boolean;
}

export const processesApi = {
  list: (filter: ListFilter = {}) =>
    api.get<{ processes: ProcessRow[] }>(BASE, filter as Record<string, unknown>),

  getOne: (id: string) => api.get<ProcessRow>(`${BASE}/${id}`),

  getGraph: (id: string) => api.get<ProcessGraph>(`${BASE}/${id}/graph`),

  listAudit: (id: string, limit = 100) =>
    api.get<{ audit: ProcessAuditEntry[] }>(`${BASE}/${id}/audit`, { limit }),

  create: (body: Partial<ProcessRow>) => api.post<ProcessRow>(BASE, body),

  update: (id: string, body: Partial<ProcessRow> & { bumpVersion?: boolean }) =>
    api.patch<ProcessRow>(`${BASE}/${id}`, body),

  remove: (id: string) => api.delete<void>(`${BASE}/${id}`),

  // Steps
  createStep: (processId: string, body: Partial<ProcessStep>) =>
    api.post<ProcessStep>(`${BASE}/${processId}/steps`, body),
  updateStep: (processId: string, stepId: string, body: Partial<ProcessStep>) =>
    api.patch<ProcessStep>(`${BASE}/${processId}/steps/${stepId}`, body),
  deleteStep: (processId: string, stepId: string) =>
    api.delete<void>(`${BASE}/${processId}/steps/${stepId}`),

  // Edges
  createEdge: (processId: string, body: Partial<ProcessEdge>) =>
    api.post<ProcessEdge>(`${BASE}/${processId}/edges`, body),
  deleteEdge: (processId: string, edgeId: string) =>
    api.delete<void>(`${BASE}/${processId}/edges/${edgeId}`),

  // Bulk graph save
  saveGraph: (
    processId: string,
    body: { steps: Array<Partial<ProcessStep> & { tempId?: string }>; edges: Array<Partial<ProcessEdge>>; flowConfig?: Record<string, unknown> },
  ) => api.put<{ idMap: Record<string, string>; stepCount: number; edgeCount: number }>(`${BASE}/${processId}/graph`, body),

  // Junctions (replace-all)
  setProcessSeats: (processId: string, items: Array<{ seatId: string; role: RaciRole }>) =>
    api.put<void>(`${BASE}/${processId}/seats`, { items }),
  setStepSeats: (processId: string, stepId: string, items: Array<{ seatId: string; role: RaciRole }>) =>
    api.put<void>(`${BASE}/${processId}/steps/${stepId}/seats`, { items }),
  setStepKpis: (processId: string, stepId: string, items: Array<{ kpiId: string; relationship: KpiRelationship }>) =>
    api.put<void>(`${BASE}/${processId}/steps/${stepId}/kpis`, { items }),
  setKpiSeats: (kpiId: string, items: Array<{ seatId: string; role: SeatKpiRole }>) =>
    api.put<void>(`${BASE}/kpi-seats/${kpiId}`, { items }),
};

export const processesKeys = {
  all: ['processes'] as const,
  list: (filter: ListFilter) => [...processesKeys.all, 'list', filter] as const,
  one: (id: string) => [...processesKeys.all, 'one', id] as const,
  graph: (id: string) => [...processesKeys.all, 'graph', id] as const,
  audit: (id: string) => [...processesKeys.all, 'audit', id] as const,
};
